import React, { useState, useEffect } from 'react';
import { fetchUsersStatistics } from '../../../services/Http/Api';
import SalesByCountries from './components/SalesByCountries';
import StatisticsSkeleton from '../../common/skeletons/StatisticsSkeleton';
import {
    MdPeople,
    MdPerson,
    MdPublic,
    MdAttachMoney,
    MdAccountBalanceWallet,
    MdAccountCircle
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import SectionTitle from '../../../shared/layout/titles/SectionTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import Row from '../../../shared/layout/containers/Row';
import Column from '../../../shared/layout/containers/Column';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import CardHeader from '../../../shared/layout/cards/CardHeader';
import CardTitle from '../../../shared/layout/cards/CardTitle';
import CardBody from '../../../shared/layout/cards/CardBody';
import ListGroup from '../../../shared/layout/containers/ListGroup';
import ListItem from '../../../shared/layout/containers/ListItem';
import ProfileContainer from '../../../shared/layout/containers/ProfileContainer';
import ProfileName from '../../../shared/layout/typography/ProfileName';
import ItemCount from '../../../shared/layout/typography/ItemCount';
import Badge from '../../../shared/layout/typography/Badge';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../context/CurrencyProvider';

const Statistics = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getStatistics = async () => {
            try {
                const data = await fetchUsersStatistics();
                setStatistics(data && Object.keys(data).length > 0 ? data : null);
            } catch (error) {
                console.error('Error fetching statistics:', error);
                toast.error(t('Users.UserStatistics.loadError'));
            } finally {
                setLoading(false);
            }
        };

        getStatistics();
    }, []);

    if (loading) return <StatisticsSkeleton />;
    if (!statistics) return <p>{t('Users.UserStatistics.noStatistics')}</p>;

    return (
        <PageContainer>
            <PageHeader>
                <SectionTitle>{t('Users.UserStatistics.overviewTitle')}</SectionTitle>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    <StatCard
                        icon={<MdPeople size={30} className="icon" />}
                        title={t('Users.UserStatistics.totalBuyers')}
                        value={statistics.total_buyers.toLocaleString()}
                        color="primary"
                    />
                    <StatCard
                        icon={<MdPerson size={30} className="icon" />}
                        title={t('Users.UserStatistics.totalBuyersOwn')}
                        value={statistics.total_buyers_own.toLocaleString()}
                        color="success"
                    />
                    <StatCard
                        icon={<MdPublic size={30} className="icon" />}
                        title={t('Users.UserStatistics.totalBuyersGlobal')}
                        value={statistics.total_buyers_global.toLocaleString()}
                        color="info"
                    />
                    <StatCard
                        icon={<MdAttachMoney size={30} className="icon" />}
                        title={t('Users.UserStatistics.totalRevenueOwn')}
                        value={formatResolvedAmount({
                            eurAmount: statistics.total_revenue_own,
                            localAmount: statistics.total_revenue_own_local,
                            localCurrencyId: statistics.currency_id,
                        })}
                        color="warning"
                    />
                    <StatCard
                        icon={<MdAccountBalanceWallet size={30} className="icon" />}
                        title={t('Users.UserStatistics.totalRevenueGlobal')}
                        value={formatResolvedAmount({
                            eurAmount: statistics.total_revenue_global,
                            localAmount: statistics.total_revenue_global_local,
                            localCurrencyId: statistics.currency_id,
                        })}
                        color="danger"
                    />
                </StatsGrid>
                <Row>
                    <Column>
                        <SalesByCountries data={statistics.summary_by_countries} />
                    </Column>
                    <Column>
                        <StyledCard>
                            <CardHeader>
                                <CardTitle>{t('Users.UserStatistics.topBuyersByItems')}</CardTitle>
                            </CardHeader>
                            <CardBody textSize="extraSmall">
                                <ListGroup>
                                    {statistics.top_buyers_by_items && statistics.top_buyers_by_items.map((buyer, index) => (
                                        <ListItem key={index}>
                                            <ProfileContainer>
                                                <MdAccountCircle size={24} color="rgba(130, 22, 146, 0.8)" />
                                                <ProfileName>{buyer.user_name} {buyer.user_surname}</ProfileName>
                                            </ProfileContainer>
                                            <ItemCount>{buyer.count_of_items} {t('Users.UserStatistics.items')}</ItemCount>
                                            <Badge ownBuyer={buyer.own_buyer}>
                                                {buyer.own_buyer ? t('Users.UserStatistics.own') : t('Users.UserStatistics.global')}
                                            </Badge>
                                        </ListItem>
                                    ))}
                                    {(!statistics.top_buyers_by_items || statistics.top_buyers_by_items.length === 0) && (
                                        <ListItem>
                                            <p style={{ padding: '0.625rem', color: '#6b7280' }}>
                                                {t('Users.UserStatistics.noData')}
                                            </p>
                                        </ListItem>
                                    )}
                                </ListGroup>
                            </CardBody>
                        </StyledCard>
                    </Column>
                    <Column half>
                        <StyledCard>
                            <CardHeader>
                                <CardTitle>{t('Users.UserStatistics.topBuyersByTotalSpent')}</CardTitle>
                            </CardHeader>
                            <CardBody textSize="extraSmall">
                                <ListGroup>
                                    {statistics.top_buyers_by_total_spent && statistics.top_buyers_by_total_spent.map((buyer, index) => (
                                        <ListItem key={index}>
                                            <ProfileContainer>
                                                <MdAccountCircle size={40} color="rgba(130, 22, 146, 0.8)" />
                                                <ProfileName>{buyer.user_name} {buyer.user_surname}</ProfileName>
                                            </ProfileContainer>
                                            <ItemCount>{formatResolvedAmount({
                                                eurAmount: buyer.total_spent,
                                                localAmount: buyer.total_spent_local,
                                                localCurrencyId: buyer.currency_id ?? statistics.currency_id,
                                            })}</ItemCount>
                                            <Badge ownBuyer={buyer.own_buyer}>
                                                {buyer.own_buyer ? t('Users.UserStatistics.own') : t('Users.UserStatistics.global')}
                                            </Badge>
                                        </ListItem>
                                    ))}
                                    {(!statistics.top_buyers_by_total_spent || statistics.top_buyers_by_total_spent.length === 0) && (
                                        <ListItem>
                                            <p style={{ padding: '0.625rem', color: '#6b7280' }}>
                                                {t('Users.UserStatistics.noData')}
                                            </p>
                                        </ListItem>
                                    )}
                                </ListGroup>
                            </CardBody>
                        </StyledCard>
                    </Column>
                </Row>
            </PageContent>
        </PageContainer>
    );
};

export default Statistics;
